<template>
  <div class="proxy-page">
    <PageHeader
      title="Residential proxies"
      subtitle="Authenticate to the residential gateway, build a request, and watch usage."
    >
      <template #actions>
        <v-btn
          text
          color="primary"
          class="proxy-page__btn"
          href="https://proxydocs.scrapingant.com"
          target="_blank"
        >
          <v-icon left small>mdi-book-open-variant-outline</v-icon>Proxy docs
          <v-icon right small>mdi-open-in-new</v-icon>
        </v-btn>
      </template>
    </PageHeader>

    <v-row>
      <v-col cols="12">
        <SectionCard title="Current subscription" class="proxy-page__card">
          <template
            v-if="
              !!residential_subscription && !!residential_subscription.plan_name
            "
          >
            <div class="proxy-page__split">
              <div class="proxy-page__split-left">
                <dl class="proxy-page__meta">
                  <div class="proxy-page__meta-row">
                    <dt>Current plan</dt>
                    <dd>
                      <router-link to="/subscriptions?tab=residential">{{
                        residential_subscription.plan_name
                      }}</router-link>
                    </dd>
                  </div>
                  <div class="proxy-page__meta-row">
                    <dt>Start date</dt>
                    <dd>{{ parseDate(residential_subscription.start_date) }}</dd>
                  </div>
                  <div class="proxy-page__meta-row">
                    <dt>
                      {{
                        residential_subscription.recurring
                          ? "Renewal date"
                          : "End date"
                      }}
                    </dt>
                    <dd>{{ parseDate(residential_subscription.end_date) }}</dd>
                  </div>
                </dl>

                <StatCard
                  class="proxy-page__stat"
                  label="Traffic used"
                  :value="residential_subscription.traffic_used_gb"
                  :max="residential_subscription.traffic_limit_gb"
                  unit="GB"
                  :caption="trafficCaption"
                />

                <p
                  v-if="
                    residential_subscription.traffic_used_gb >=
                    residential_subscription.traffic_limit_gb
                  "
                  class="proxy-page__hint"
                >
                  You've run out of traffic. Please
                  <router-link to="/subscriptions?tab=residential"
                    >upgrade your plan</router-link
                  >
                  or
                  <a href="mailto:support@scrapingant.com">contact us</a>.
                </p>
              </div>

              <div class="proxy-page__split-right">
                <CredentialField
                  label="Username"
                  :value="residential_credentials.proxy_username || ''"
                />
                <CredentialField
                  label="Password"
                  :value="residential_credentials.proxy_secret || ''"
                  secret
                />
              </div>
            </div>
          </template>
          <template v-else>
            <p class="proxy-page__empty">
              No active subscription.
              <router-link :to="{ path: '/subscriptions?tab=residential' }"
                >Subscribe</router-link
              >
              to get residential proxy access.
            </p>
          </template>
          <template
            v-if="
              residential_subscription &&
              residential_subscription.update_url &&
              residential_subscription.recurring
            "
            #actions
          >
            <v-btn
              text
              color="primary"
              class="proxy-page__btn"
              @click="onUpdatePaymentDetailsClicked"
            >Update payment</v-btn>
          </template>
        </SectionCard>
      </v-col>
    </v-row>

    <SectionCard
      v-if="residential_credentials && residential_credentials.proxy_username"
      title="Request generator"
      subtitle="Tweak country / session / state / city and grab a curl snippet."
      class="proxy-page__generator mt-2"
    >
      <v-row>
        <v-col cols="12" lg="6">
          <v-text-field
            hide-details
            label="URL"
            v-model="url"
            outlined
            dense
            class="mb-4"
          ></v-text-field>
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                hide-details
                label="Proxy country"
                v-model="request_generator.country"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                hide-details
                label="Session ID"
                v-model="request_generator.sessionid"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                hide-details
                label="State"
                v-model="request_generator.state"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                hide-details
                label="City"
                v-model="request_generator.city"
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" lg="6">
          <CodeBlock
            :code="templates[0].get_template"
            language="bash"
            label="cURL · GET request"
          />
        </v-col>
      </v-row>
    </SectionCard>

    <SectionCard
      title="Residential traffic usage"
      subtitle="GBs consumed since the current subscription started."
      class="proxy-page__chart mt-2"
    >
      <div class="proxy-page__chart-wrap">
        <usage-chart
          v-if="!usage_grapph_loading"
          :start-date="parseDate(residential_subscription.start_date)"
          :chart-data="residential_chartdata"
          :height="260"
        />
        <div v-else class="proxy-page__chart-loader">
          <v-progress-circular
            :size="40"
            :width="2"
            color="primary"
            indeterminate
          ></v-progress-circular>
        </div>
      </div>
    </SectionCard>

    <div class="loading-overlay" v-if="loading">
      <v-progress-circular
        :size="50"
        :width="2"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </div>
  </div>
</template>

<script>
import UsageChart from "../views/UsageChart";
import getDateUTCString from "@/utils/date_utils";
import { proxyTemplates } from "@/helper/proxy-template-helper";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import CredentialField from "@/components/shared/CredentialField";
import CodeBlock from "@/components/shared/CodeBlock";

const PRIMARY = "#E2782F";
const PRIMARY_ALPHA = "#E2782F99";
const TRANSPARENT = "#FFFFFF00";

const buildPrimaryDataset = (label, data) => ({
  label,
  lineTension: 0.2,
  borderWidth: 2,
  pointRadius: 2,
  backgroundColor: TRANSPARENT,
  borderColor: PRIMARY,
  pointBorderColor: PRIMARY,
  pointBackgroundColor: PRIMARY,
  data,
});

const buildSecondaryDataset = (label, data, i) => ({
  label,
  lineTension: 0.2,
  borderWidth: 2,
  pointRadius: 2,
  backgroundColor: PRIMARY_ALPHA,
  borderColor: PRIMARY_ALPHA,
  pointBorderColor: PRIMARY_ALPHA,
  pointBackgroundColor: PRIMARY_ALPHA,
  borderDash: [(i + 1) * 2, i + 1],
  data,
});

export default {
  name: "ResidentialProxies",
  components: {
    UsageChart,
    PageHeader,
    SectionCard,
    StatCard,
    CredentialField,
    CodeBlock,
  },
  data() {
    return {
      request_generator: {
        country: "",
        sessionid: "",
        state: "",
        city: "",
      },
      url: "https://example.com",
      residential_subscription: {},
      residential_credentials: {},
      residential_chartdata: {
        labels: [],
        datasets: [buildPrimaryDataset("GBs used", [])],
      },
      loading: false,
      usage_grapph_loading: false,
    };
  },
  computed: {
    residentialUsageValue() {
      return this.residential_subscription
        ? (this.residential_subscription.traffic_used_gb /
            this.residential_subscription.traffic_limit_gb) *
            100
        : 0;
    },
    trafficCaption() {
      if (
        !this.residential_subscription ||
        !this.residential_subscription.traffic_limit_gb
      )
        return "";
      return `${this.residential_subscription.traffic_used_gb.toLocaleString()} of ${this.residential_subscription.traffic_limit_gb.toLocaleString()} GB used`;
    },
    templates() {
      return [
        {
          name: "curl",
          get_template: proxyTemplates.CURL.get(
            this.request_generator,
            this.residential_credentials["proxy_username"],
            this.residential_credentials["proxy_secret"],
            this.url
          ),
          prism_language: "bash",
        },
      ];
    },
  },
  methods: {
    getResidentialUsageGraph() {
      this.usage_grapph_loading = true;
      return this.$store.dispatch("dashboard/getResidentialUsageGraph").then(
        (res) => {
          this.usage_grapph_loading = false;
          const next = Object.assign({}, this.residential_chartdata);
          next.labels = res.data.labels;
          next.datasets = [buildPrimaryDataset("GBs used", res.data.data)];
          if (res.data.detailed_view) {
            res.data.detailed_view.forEach((userData, i) => {
              next.datasets.push(
                buildSecondaryDataset(userData.email, userData.data, i)
              );
            });
          }
          this.residential_chartdata = next;
        },
        () => {
          this.usage_grapph_loading = false;
        }
      );
    },
    getCurrentResidentialSubscription() {
      return this.$store
        .dispatch("dashboard/getCurrentResidentialSubscription")
        .then((res) => {
          this.residential_subscription = res;
        }, console.error);
    },
    getResidentialCredentials() {
      return this.$store
        .dispatch("dashboard/getResidentialCredentials")
        .then((res) => {
          this.residential_credentials = res;
        }, console.error);
    },
    parseDate(date) {
      return getDateUTCString(new Date(date));
    },
    reload() {
      this.$router.go();
    },
    onUpdatePaymentDetailsClicked() {
      // eslint-disable-next-line no-undef
      Paddle.Checkout.open({
        override: this.residential_subscription.update_url,
        successCallback: () => {},
        closeCallback: () => {},
      });
    },
  },
  mounted() {
    this.loading = true;
    Promise.all([
      this.getCurrentResidentialSubscription(),
      this.getResidentialCredentials(),
    ]).then(
      () => (this.loading = false),
      () => (this.loading = false)
    );
    this.getResidentialUsageGraph();
  },
};
</script>

<style scoped lang="scss">
.proxy-page {
  position: relative;

  &__card {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  &__meta {
    margin: 0 0 16px;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__meta-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 13px;
    line-height: 1.5;

    dt {
      color: rgba(15, 23, 42, 0.58);
      font-weight: 500;
      margin: 0;
    }

    dd {
      color: #0f172a;
      font-weight: 500;
      margin: 0;
      text-align: right;

      a {
        color: #e2782f;
        font-weight: 600;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }

  &__split {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 32px;
    align-items: start;

    @media (max-width: 960px) {
      grid-template-columns: 1fr;
      gap: 24px;
    }
  }

  &__split-left {
    display: flex;
    flex-direction: column;
  }

  &__split-right {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__stat {
    margin-top: 8px;
  }

  &__hint {
    font-size: 12px;
    color: rgba(15, 23, 42, 0.62);
    line-height: 1.5;
    margin: 14px 0 0;

    a {
      color: #e2782f;
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__empty {
    font-size: 14px;
    color: rgba(15, 23, 42, 0.62);
    margin: 0;

    a {
      color: #e2782f;
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__btn {
    text-transform: none !important;
    letter-spacing: 0 !important;
    font-weight: 500 !important;
  }

  &__chart-wrap {
    position: relative;
    height: 260px;
    width: 100%;
    overflow: hidden;

    ::v-deep > div {
      height: 100% !important;
      width: 100% !important;
    }

    ::v-deep canvas {
      max-height: 100%;
    }
  }

  &__chart-loader {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
